<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="dialogStore.promptDialog.isOpen"
        class="modal-overlay modal-overlay-dialog"
        @click.self="handleCancel"
      >
        <div class="modal-dialog">
          <div class="modal-header modal-header-compact">
            <h3>{{ dialogStore.promptDialog.options.title || 'Input' }}</h3>
          </div>
          <div class="modal-body">
            <p>{{ dialogStore.promptDialog.options.message }}</p>
            <input
              ref="inputRef"
              v-model="inputValue"
              type="text"
              class="modal-input"
            >
          </div>
          <div class="modal-footer">
            <button
              class="modal-button modal-button-secondary"
              @click="handleCancel"
            >
              {{ dialogStore.promptDialog.options.cancelText || 'Cancel' }}
            </button>
            <button
              class="modal-button modal-button-primary"
              @click="handleConfirm"
            >
              {{ dialogStore.promptDialog.options.confirmText || 'OK' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onUnmounted } from 'vue';
import { useDialogStore } from '../stores/dialogs';

const dialogStore = useDialogStore();
const inputValue = ref('');
const inputRef = ref<HTMLInputElement | null>(null);

const handleConfirm = (): void => {
  const value = inputValue.value.trim();
  dialogStore.resolvePrompt(value || null);
  inputValue.value = '';
};

const handleCancel = (): void => {
  dialogStore.resolvePrompt(null);
  inputValue.value = '';
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (!dialogStore.promptDialog.isOpen) return;

  if (event.key === 'Escape') {
    event.preventDefault();
    handleCancel();
  } else if (event.key === 'Enter') {
    event.preventDefault();
    handleConfirm();
  }
};

// Watch for dialog opening and set default value + focus
watch(
  () => dialogStore.promptDialog.isOpen,
  async (isOpen) => {
    if (isOpen) {
      inputValue.value = dialogStore.promptDialog.options.defaultValue || '';
      await nextTick();
      inputRef.value?.focus();
      inputRef.value?.select();
      window.addEventListener('keydown', handleKeydown);
    } else {
      window.removeEventListener('keydown', handleKeydown);
    }
  }
);

// Cleanup on unmount
onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
});
</script>

<style scoped>
/* Modal dialog now uses global .modal-dialog utility class */
/* Modal structure now uses global utilities (modal-header-compact, modal-body, modal-footer, modal-input) */
/* Custom override: modal-body paragraph margin */
.modal-body p {
  margin: 0 0 12px 0;
}

/* Modal buttons now use global .modal-button utility classes */
/* Note: PromptDialog uses different secondary color - override here */
.modal-button-secondary {
  background: var(--color-dark-border-primary);
}

.modal-button-secondary:hover {
  background: var(--color-dark-border-secondary);
}

/* Modal transitions now use global .modal-enter-active/.modal-leave-active classes */
</style>
